<template>
  <div class="page-calendar">
    <div style="font-size: 12px; text-align: right;">
      ({{ $t("Timezone") }}: {{ calendarOptions && calendarOptions.timeZone }})
    </div>
    <FullCalendar v-if="showCalendar" ref="calendar" :options="calendarOptions">
      <template v-slot:eventContent="arg">
        <b>{{ arg.timeText }}</b>
        <i>{{ arg.event.title }}</i>
      </template>
    </FullCalendar>
    <Notifications />
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import frLocale from "@fullcalendar/core/locales/fr";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import {
  getPilotAvailableSchedule,
  getPackage
} from "@/services/flightReview.service.js";
import { DEFAULT_TIMEZONE } from "@/const.js";
import { secondsToHoursAndMinutes, generateEvents } from "@/helpers/utils";
import { mapFields } from "vuex-map-fields";
import swal from "sweetalert2";

export default {
  components: {
    FullCalendar // make the <FullCalendar> tag available
  },
  $_veeValidate: {
    validator: "new"
  },
  computed: {
    ...mapFields("flightReview", ["booking"])
  },
  data: function() {
    const locale = window.localStorage.getItem("locale") || "en";
    return {
      showCalendar: false, // to fix first time calendar style mass
      calendarEvents: [],
      calendarOptions: {
        locales: [esLocale, frLocale],
        locale,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          momentTimezonePlugin
        ],
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay"
        },
        initialView: "timeGridWeek",
        events: [],
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        // select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        displayEventTime: false,
        timeZone: DEFAULT_TIMEZONE
      },
      reviewerId: ""
    };
  },
  methods: {
    showCalendarFn(status) {
      this.showCalendar = status;
      if (status && this.booking.alwaysAvailable) {
        swal.fire({
          text: this.$t(
            "This reviewer prefers to schedule times directly. After successfully booking your review, please contact the reviewer to arrange a specific time."
          ),
          confirmButtonClass: "md-button md-primary",
          buttonsStyling: false
        });
      }
    },
    validate() {
      if (this.booking.reviewStartDate || this.booking.alwaysAvailable) {
        return true;
      } else {
        this.$notify({
          message: this.$t("Please select a flight review time"),
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "warning",
          timeout: 3000
        });
      }
    },

    handleWeekendsToggle() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends; // update a property
    },
    handleEventClick(clickInfo) {
      if (clickInfo.el.classList.contains("available-event")) {
        // reset
        this.calendarOptions.events.map(item => {
          item.classNames = ["available-event"];
        });

        this.calendarOptions.events.map(item => {
          if (+item.start === +clickInfo.event.start) {
            item.classNames = ["booked-future"];
          }
        });

        this.calendarOptions.events = [...this.calendarOptions.events];

        this.$store.commit("flightReview/updateBooking", {
          reviewStartDate: +clickInfo.event.start / 1000,
          reviewEndDate: +clickInfo.event.end / 1000
        });
        this.$notify({
          message: this.$t(
            "Timeslot selected. Please click Next button to proceed."
          ),
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success",
          timeout: 3000
        });
      }
    },
    async initialSchedule(reviewerId) {
      // get package
      const res = await getPackage(reviewerId);
      const info = res || {};
      if (info._id && info._id.$oid) {
        this.$store.commit("flightReview/updateBooking", {
          packageId: info._id.$oid
        });
        this.$store.commit("flightReview/updatePackageInfo", info);
      }
      // get schedule
      this.reviewerId = reviewerId;
      const {
        events,
        schedule = [],
        review_timezone,
        always_available
      } = await getPilotAvailableSchedule(reviewerId);
      let timezone = review_timezone || DEFAULT_TIMEZONE;
      // always available
      if (always_available) {
        this.calendarOptions.timeZone = timezone;
        this.$store.commit("flightReview/updateBooking", {
          alwaysAvailable: true
        });
        return;
      }
      // time slot
      this.$store.commit("flightReview/updateBooking", {
        alwaysAvailable: false
      });
      let startTime, endTime;
      let futureAvailableAll = [];
      for (let item of schedule) {
        const availableSchedule = item.available_schedule;
        if (availableSchedule) {
          let weeklyIdx = [];
          delete availableSchedule.repeat;
          for (let key in availableSchedule) {
            let _availableSchedule = availableSchedule[key];
            if (_availableSchedule.available) {
              startTime = secondsToHoursAndMinutes(
                _availableSchedule.start_time
              );
              endTime = secondsToHoursAndMinutes(_availableSchedule.end_time);
              weeklyIdx.push(key);
            }
          }
          const { futureAvailable } = generateEvents({
            daysOfWeek: weeklyIdx,
            startTime,
            endTime,
            startDate: item.start_time.$date,
            endDate: item.end_time.$date,
            events,
            timezone,
            duration: item.duration || 60
          });
          futureAvailableAll = futureAvailableAll.concat(futureAvailable);
        }
      }
      this.calendarOptions.timeZone = timezone;
      this.calendarOptions.events = [...futureAvailableAll];
    }
  },
  mounted() {
    if (this.$store.state.flightReview.booking.reviewerId) {
      this.initialSchedule(this.$store.state.flightReview.booking.reviewerId);
    }
  },
  watch: {
    "$store.state.flightReview": {
      handler: async function(newVal) {
        if (newVal.booking.reviewerId === this.reviewerId) return;
        this.initialSchedule(newVal.booking.reviewerId);
      },
      deep: true
    }
  }
};
</script>

<style lang="css">
.page-calendar {
  .booked-past {
    background-color: #ccc;
    cursor: not-allowed;
  }
  .booked-future {
    background-color: #3788d8;
    cursor: not-allowed;
  }
  .available-event {
    background-color: #4caf50;
    cursor: pointer;
  }
}
</style>
